import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { SITE } from "@/lib/site";

export default function BrandShowcase() {
  const anchor = SITE.brands.find((b) => b.featured);
  const rest = SITE.brands.filter((b) => !b.featured);

  return (
    <section
      id="partners"
      data-testid="brand-showcase"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute -left-40 top-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-[#1d52c4]/25 blur-[140px] pointer-events-none" />
      <div className="absolute -right-40 top-1/4 h-[400px] w-[400px] rounded-full bg-[#22d3ee]/15 blur-[140px] pointer-events-none" />

      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14 md:mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-12 font-display text-4xl md:text-6xl tracking-tighter leading-[0.98]"
          >
            Only genuine, warranty-backed.{" "}
            <span className="italic font-light text-white/60">
              India's top electrical names, all under one roof.
            </span>
          </motion.h2>
        </div>

        {/* Anchor featured card */}
        {anchor && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`brand-card-anchor`}
            className="glass-strong relative overflow-hidden rounded-[32px] p-8 md:p-12 mb-8 border-[#22d3ee]/25"
          >
            <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[#22d3ee]/25 blur-[100px]" />
            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <div className="inline-flex items-center gap-2 font-mono-tag text-[10px] uppercase text-[#22d3ee] mb-5">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {anchor.badge}
                </div>
                <p className="text-white/70 text-sm md:text-base max-w-[52ch]">
                  {anchor.tag} — every product is 100% genuine & warranty-backed.
                  Direct from the authorised distributor, no substitutes.
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <div className="inline-flex items-center justify-center bg-white rounded-2xl px-6 py-4 shadow-[0_20px_60px_-20px_rgba(34,211,238,0.4)]">
                  <img
                    src={anchor.logo}
                    alt="Anchor by Panasonic"
                    className="h-14 md:h-16 w-auto object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {rest.map((b, i) => (
            <motion.div
              key={b.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{
                duration: 0.7,
                delay: i * 0.05,
                ease: [0.22, 1, 0.36, 1],
              }}
              whileHover={{ y: -6 }}
              data-testid={`brand-card-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="glass-strong group relative overflow-hidden rounded-2xl h-[200px] md:h-[220px] flex flex-col items-center justify-center px-4 hover:border-white/25 transition-[border-color] duration-500"
            >
              <div className="absolute -inset-16 bg-[radial-gradient(circle_at_50%_120%,rgba(34,211,238,0.4),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10 flex flex-col items-center justify-center h-full w-full">
                {/* White pill for logo so brand colors survive on dark bg */}
                <div className="flex items-center justify-center bg-white/95 rounded-xl px-4 py-3 h-[86px] w-[86%] mb-4">
                  <img
                    src={b.logo}
                    alt={b.name}
                    loading="lazy"
                    className="max-h-[58px] max-w-full object-contain"
                  />
                </div>
                <div className="text-center">
                  <div className="text-white/85 text-sm font-display tracking-tight">
                    {b.name}
                    {b.sub && (
                      <span className="text-white/40 italic ml-1 text-xs">
                        · {b.sub}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-[10px] text-white/45">{b.tag}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
